import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useApi } from '@/utils/axios'
import { AUTENTICACAO_ROTAS, USUARIO_ROTAS } from '@/utils/apiRoutes'
import { setSentryUser } from '@/utils/sentry'

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref(localStorage.getItem('token') || null);
  const user = ref(JSON.parse(localStorage.getItem('user') || 'null'));
  const loading = ref(false);
  const error = ref(null);
  const pollingInterval = ref(null);
  // Computed
  const isAuthenticated = computed(() => !!token.value)

  // Actions
  const login = async (credentials) => {
    try {
      // Call login API
      const response = await useApi(AUTENTICACAO_ROTAS.LOGIN).post(credentials)

      if (response.data && response.data.access_token) {
        // Store token and user data
        token.value = response.data.access_token
        user.value = response.data.user

        console.log('response.data.user', response.data.user)

        setSentryUser({
          id: response.data.user.id,
          email: response.data.user.email,
          name: response.data.user.nome,
        })

        // Save to localStorage for persistence
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Start polling after successful login
        startPolling();
        
        return { success: true };
      }

      return { success: false }
    } catch (error) {
      // Check for specific status codes
      if (error.response) {
        if (error.response.status === 403) {
          return {
            success: false,
            errorCode: 403,
            message: 'Usuário desativado no sistema. Entre em contato com o administrador.',
          }
        } else if (error.response.status === 401) {
          return {
            success: false,
            errorCode: 401,
            message: 'Credenciais inválidas. Por favor, verifique seu e-mail e senha.',
          }
        } else if (error.response.status === 422) {
          return {
            success: false,
            errorCode: 422,
            message: error.response.data.message ?? 'Preencha os dados corretamente.',
          }
        }
      }

      return {
        success: false,
        errorCode: error.response?.status || 500,
        message: 'Erro ao tentar realizar login.',
      }
    }
  }

  const logout = async () => {
    try {
      // Stop polling before logout
      stopPolling();
      
      // Call the logout endpoint before clearing local data
      const { post } = useApi(AUTENTICACAO_ROTAS.LOGOUT)
      await post()
    } catch (error) {
    } finally {
      // Clear local data regardless of API call result
      token.value = null
      user.value = null

      localStorage.removeItem('token')
      localStorage.removeItem('access_token')
      localStorage.removeItem('user')
    }
  }

  // Adicionar este método ao store
  const updateUserData = (userData) => {
    user.value = userData
  }

  /**
   * Extends the current session when user chooses to stay active
   */
  const extendSession = async () => {
    try {
      const { post } = useApi(AUTENTICACAO_ROTAS.PERMANECER_ATIVO)
      const response = await post({
        action: 'continue_session',
        timestamp: new Date().toISOString(),
      })

      if (response && response.status === 'success' && response.data) {
        const { access_token, token_type, expires_in, user: userData } = response.data

        if (access_token) {
          // Update token in localStorage
          token.value = access_token
          localStorage.setItem('access_token', access_token)
          localStorage.setItem('token', access_token)

          if (userData) {
            // Update user data
            updateUserData(userData)
            localStorage.setItem('user', JSON.stringify(userData))
          }

          return true
        }
      }
      return false
    } catch (error) {
      return false
    }
  }

  /**
   * Busca as páginas que o usuário atual tem acesso
   */
  async function fetchUserPages() {
    loading.value = true
    error.value = null

    try {
      if (!user.value || !user.value.id) {
        return false
      }

      const response = await useApi(USUARIO_ROTAS.PAGINAS(user.value.id)).fetch({
        params: { estado: 1 },
      })

      if (response && response.data) {
        updateUserPages(response.data)
        return true
      }

      return false
    } catch (err) {
      error.value = err.message || 'Erro ao carregar páginas'
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Atualiza as páginas do usuário no store e no localStorage
   */
  function updateUserPages(newPages) {
    if (user.value) {
      user.value.paginas = newPages
      // Atualiza também no localStorage para persistir as alterações
      localStorage.setItem('user', JSON.stringify(user.value))
      return true
    } else {
      return false
    }
  }

  /**
   * Inicia o polling para atualizar o timestamp do usuário logado
   */
  const startPolling = () => {
    // Para o polling anterior se existir
    stopPolling();
    
    // Inicia novo polling a cada 15 segundos
    pollingInterval.value = setInterval(async () => {
      try {
        if (user.value && user.value.id) {
          await useApi(USUARIO_ROTAS.USUARIOS_LOGADOS).post({
            sys_usuario_id: user.value.id
          });
        }
      } catch (error) {
        // Silently handle polling errors to avoid console spam
        console.warn('Erro no polling de atualização de timestamp:', error);
      }
    }, 15000); // 15 segundos
  };

  /**
   * Para o polling de atualização do timestamp
   */
  const stopPolling = () => {
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value);
      pollingInterval.value = null;
    }
  };

  return {
    token,
    user,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    updateUserData,
    extendSession,
    fetchUserPages,
    updateUserPages,
    startPolling,
    stopPolling
  };
});
